<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.client-name')"
                v-model.trim="filterOptions.user_name"
              />

              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.userMobile')"
                v-model.trim="filterOptions.user_mobile"
              />

              <base-input
                col="3"
                type="text"
                :placeholder="$t('PLACEHOLDERS.orderNumber')"
                v-model.trim="filterOptions.order_number"
              />

              <base-select-input
                col="3"
                :placeholder="$t('PLACEHOLDERS.rating_stars')"
                v-model="filterOptions.rate"
                :optionsList="[
                  { id: 1, value: 1, name: '1' },
                  { id: 2, value: 2, name: '2' },
                  { id: 3, value: 3, name: '3' },
                  { id: 4, value: 4, name: '4' },
                  { id: 5, value: 5, name: '5' },
                ]"
              />

              <base-picker-input
                col="6"
                type="date"
                :placeholder="$t('PLACEHOLDERS.data_from')"
                v-model.trim="filterOptions.from_date"
              />

              <base-picker-input
                col="6"
                type="date"
                :placeholder="$t('PLACEHOLDERS.data_to')"
                v-model.trim="filterOptions.to_date"
              />
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Statistics Section =========== -->
      <div class="statistics_section">
        <div class="row">
          <div class="col-md-6">
            <div class="stat_card">
              <div class="stat_icon">
                <i class="fas fa-star"></i>
              </div>
              <div class="stat_content">
                <h3>{{ statistics.average_rating || 0 }}</h3>
                <p>{{ $t("TABLES.ProviderRates.averageRating") }}</p>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="stat_card">
              <div class="stat_icon">
                <i class="fas fa-user"></i>
              </div>
              <div class="stat_content">
                <h3>{{ statistics.total_reviews || 0 }}</h3>
                <p>{{ $t("TABLES.ProviderRates.totalReviews") }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!--  =========== End:: Statistics Section =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("SIDENAV.ProviderRates.title") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- Start:: No Data State -->

        <template v-slot:[`item.id`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>

        <!-- Start:: User Name -->
        <template v-slot:[`item.user_name`]="{ item }">
          <h6 class="text-danger" v-if="!item.user_name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.user_name }}</h6>
        </template>
        <!-- End:: User Name -->

        <!-- Start:: Order ID -->
        <template v-slot:[`item.order`]="{ item }">
          <!-- {{ item.order }} -->
          <router-link
            v-if="item.order?.serial_number !== null"
            :to="`/orders/show/${item.order?.id}`"
            class="order_link"
          >
            {{ item.order?.id }}
            <i class="fal fa-link"></i>
          </router-link>
          <h6 class="text-danger" v-else>-</h6>
        </template>
        <!-- End:: Order ID -->

        <!-- Start:: User Mobile -->
        <template v-slot:[`item.user_mobile`]="{ item }">
          <h6 class="text-danger" v-if="!item.user_mobile">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.user_mobile }}</h6>
        </template>
        <!-- End:: User Mobile -->

        <!-- Start:: Provider Name -->
        <template v-slot:[`item.provider_name`]="{ item }">
          <h6 class="text-danger" v-if="!item.provider_name">
            {{ $t("TABLES.noData") }}
          </h6>
          <h6 v-else>{{ item.provider_name }}</h6>
        </template>
        <!-- End:: Provider Name -->

        <!-- Start:: Rate -->
        <template v-slot:[`item.rate`]="{ item }">
          <div class="rate_wrapper" style="pointer-events: none">
            <h6 v-if="!item.rate">-</h6>
            <base-rate-input
              v-else
              :value="item.rate"
              :readonly="true"
              :size="24"
            />
          </div>
        </template>
        <!-- End:: Rate -->

        <!-- Start:: Comment Preview -->
        <template v-slot:[`item.message`]="{ item }">
          <h6 v-if="!item.message">-</h6>
          <div class="actions" v-else>
            <button class="btn_show" @click="showCommentModal(item.message)">
              <i class="fal fa-file-alt"></i>
            </button>
          </div>
        </template>
        <!-- End:: Comment Preview -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>
            <!-- <a-tooltip placement="bottom" v-if="!item.is_published">
              <template slot="title">
                <span>{{ $t("BUTTONS.publishRating") }}</span>
              </template>
              <button
                class="btn_activate"
                @click="publishRating(item)"
                :disabled="isWaitingRequest"
              >
                <i class="fal fa-check-circle"></i>
              </button>
            </a-tooltip>
            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.delete") }}</span>
              </template>
              <button
                class="btn_delete"
                @click="selectDeleteRating(item)"
                :disabled="isWaitingRequest"
              >
                <i class="fal fa-trash-alt"></i>
              </button>
            </a-tooltip> -->
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Comment Modal -->
          <description-modal
            v-if="dialogComment"
            :modalIsOpen="dialogComment"
            :modalDesc="selectedCommentTextToShow"
            @toggleModal="dialogComment = !dialogComment"
          />
          <!-- End:: Comment Modal -->

          <!-- Start:: Delete Modal -->
          <v-dialog v-model="dialogDelete" max-width="500">
            <v-card>
              <v-card-title class="text-h5 justify-center">
                {{ $t("TITLES.DeleteProviderRate") }}
              </v-card-title>
              <v-card-text class="text-center">
                {{ $t("MESSAGES.deleteRatingConfirmation") }}
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  class="modal_confirm_btn"
                  @click="confirmDeleteRating"
                  :disabled="isWaitingRequest"
                >
                  {{ $t("BUTTONS.yes") }}
                </v-btn>
                <v-btn
                  class="modal_cancel_btn"
                  @click="dialogDelete = false"
                  :disabled="isWaitingRequest"
                >
                  {{ $t("BUTTONS.cancel") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Delete Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AllProviderRates",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Statistics Data
      statistics: {
        average_rating: 0,
        total_reviews: 0,
      },
      // End:: Statistics Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        user_name: null,
        user_mobile: null,
        provider_name: null,
        order_number: null,
        rate: null,
        from_date: null,
        to_date: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.ProviderRates.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.orderNumber"),
          value: "order",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.ProviderRates.userName"),
          value: "user_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.ProviderRates.userMobile"),
          value: "user_mobile",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.ProviderRates.providerName"),
          value: "provider_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.rating_stars"),
          value: "rate",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.ProviderRates.comment"),
          value: "message",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.created_at"),
          value: "created_at",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("TABLES.ProviderRates.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Control Data
      dialogComment: false,
      selectedCommentTextToShow: "",
      dialogDelete: false,
      itemToDelete: null,
      // End:: Dialogs Control Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/provider-rates/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.user_name = null;
      this.filterOptions.user_mobile = null;
      this.filterOptions.provider_name = null;
      this.filterOptions.order_number = null;
      this.filterOptions.rate = null;
      this.filterOptions.from_date = null;
      this.filterOptions.to_date = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({
          path: "/provider-rates/all",
          query: { page: 1 },
        });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Products
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "providerRates",
          params: {
            page: this.paginations.current_page,
            userName: this.filterOptions.user_name,

            userMobile: this.filterOptions.user_mobile,
            provider_name: this.filterOptions.provider_name,
            serialNumber: this.filterOptions.order_number,
            rate: this.filterOptions.rate?.value,
            DateFrom: this.filterOptions.from_date,
            DateTo: this.filterOptions.to_date,
            is_published: 1, // Only fetch published/approved reviews
          },
        });
        this.loading = false;

        // Update statistics
        if (res.data) {
          this.statistics.average_rating = res.data.avg_rate || 0;
          this.statistics.total_reviews = res.data.count_rate || 0;
        }

        // Transform the data to match table structure
        const transformedData = res.data.data?.data?.map((item, index) => {
          return {
            id: item.id,
            user_name: item.user?.name || "-",
            user_mobile: item.user?.mobile || "-",
            provider_name: item.provider?.name || "-",
            rate: item.rate,
            message: item.message || null,
            created_at: item.created_at,
            order_id: item.order_id,
            order:item.order,
            order_serial: item.order?.serial_number || null,
            is_published:
              item.is_published || item.status === "published" || false,
            // Keep original data for detail page
            originalData: item,
          };
        });

        this.tableRows = transformedData || [];
        this.paginations.last_page = res.data.data?.meta?.last_page || 1;
        this.paginations.items_per_page = res.data.data?.meta?.per_page || 10;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Set Table Rows

    // Start:: Control Modals
    showCommentModal(comment) {
      this.dialogComment = true;
      this.selectedCommentTextToShow = comment;
    },
    // End:: Control Modals

    // Start:: Truncate Comment
    truncateComment(comment) {
      if (!comment) return "-";
      return comment.length > 50 ? comment.substring(0, 50) + "..." : comment;
    },
    // End:: Truncate Comment

    // ==================== Start:: Crud ====================
    showItem(item) {
      this.$router.push({ path: `/provider-rates/show/${item.id}` });
    },

    // Start:: Publish Rating
    async publishRating(item) {
      this.isWaitingRequest = true;
      try {
        await this.$axios({
          method: "POST",
          url: `providerRates/${item.id}/publish`,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.ratingPublishedSuccessfully"));
        // Refresh table data
        this.setTableRows();
      } catch (error) {
        this.isWaitingRequest = false;
        this.$message.error(
          error?.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Publish Rating

    // Start:: Delete Rating
    selectDeleteRating(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },
    async confirmDeleteRating() {
      if (!this.itemToDelete) return;
      this.isWaitingRequest = true;
      try {
        await this.$axios({
          method: "DELETE",
          url: `providerRates/${this.itemToDelete.id}`,
        });
        this.dialogDelete = false;
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
        // Refresh table data
        this.setTableRows();
        this.itemToDelete = null;
      } catch (error) {
        this.dialogDelete = false;
        this.isWaitingRequest = false;
        this.$message.error(
          error?.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
        this.itemToDelete = null;
      }
    },
    // End:: Delete Rating
    // ==================== End:: Crud ====================
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.comment_preview {
  margin-right: 10px;
  color: #666;
  font-size: 14px;
}

/* Statistics Section Styles */
.statistics_section {
  /* padding: 20px 0; */
}

.stat_card {
  background: linear-gradient(135deg, #1b706f 0%, #2d9896 100%);
  border-radius: 12px;
  padding: 25px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(27, 112, 111, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  margin-bottom: 15px;
}

.stat_card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(27, 112, 111, 0.25);
}

.stat_icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
}

.stat_icon i {
  font-size: 32px;
  color: #ffffff;
}

.stat_content {
  flex: 1;
  color: #ffffff;
}

.stat_content h3 {
  font-size: 36px;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.stat_content p {
  font-size: 16px;
  margin: 5px 0 0 0;
  opacity: 0.9;
  font-weight: 500;
}

/* RTL Support for Arabic */
[dir="rtl"] .stat_icon {
  margin-right: 0;
  margin-left: 20px;
}

/* Order Link Styles */
.order_link {
  color: #1b706f;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;
}

.order_link:hover {
  color: #2d9896;
  text-decoration: underline;
}

.order_link i {
  font-size: 14px;
}

.rate_wrapper .my-5 {
  margin: 0 !important;
}
.rate_wrapper .input_wrapper.top_label {
  display: flex !important;
}
</style>
